import { useCallback } from 'react';
import { useWizard } from 'react-use-wizard';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle } from 'lucide-react';

import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { getStepIndex } from '@/features/guided-financial-planning/config';
import { useGuidedPlanningFlatStepConfigs } from '@/features/guided-financial-planning/context';
import { useSkipPolicySteps } from '@/features/guided-financial-planning/hooks';
import { selectClient, selectFeatureFlags } from '@client/model/selectors';
import {
  CoverageAssessmentResults,
  useCoverageAssessmentVariant,
} from './coverage-assessment-results';

export function AssessmentProjectionStep() {
  const { nextStep, previousStep, goToStep } = useWizard();
  const { hasGap } = useCoverageAssessmentVariant();
  const featureFlags = useSelector(selectFeatureFlags);
  const client = useSelector(selectClient);
  const navigate = useNavigate();
  const isCcrcTypeA = !!featureFlags.ccrcTypeAProducts;
  const isGenworthMode = !!featureFlags.genworthMode;
  const skipPolicySteps = useSkipPolicySteps();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();

  // Genworth doesn't sell or shop policies, so the coverage assessment is the last step —
  // the CTA returns to the funding plan instead of advancing into policy selection.
  const handleNext = useCallback(() => {
    if (isGenworthMode && client) {
      navigate(`/clients/${client.clientId}/onboarding/funding-sources`);
    } else if (skipPolicySteps) {
      goToStep(getStepIndex('plan-comparison', flatStepConfigs));
    } else {
      nextStep();
    }
  }, [isGenworthMode, client, navigate, skipPolicySteps, goToStep, nextStep, flatStepConfigs]);

  const nextButtonLabel = isGenworthMode
    ? 'Back to My Funding Plan'
    : isCcrcTypeA
      ? 'Show Me My Type A Plan'
      : skipPolicySteps
        ? 'Continue to Plan Comparison'
        : hasGap
          ? 'Yes, Find Insurance to Close the Gap'
          : 'Show Me Efficient Protection Options';

  const icon = hasGap ? (
    <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full p-2">
      <AlertTriangle className="text-primary size-8" />
    </div>
  ) : (
    <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full p-2">
      <CheckCircle className="text-primary size-8" />
    </div>
  );

  return (
    <StepLayout
      title="Your Coverage Assessment"
      subtitle=""
      icon={icon}
      nextButtonLabel={nextButtonLabel}
      onNext={handleNext}
      onBack={previousStep}
    >
      <CoverageAssessmentResults />
    </StepLayout>
  );
}
